import { useEffect, useState } from 'react'
import { Formik, Form } from 'formik'
import { Info, PlayCircle } from '@phosphor-icons/react'
import ReactTooltip from 'react-tooltip'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import PropTypes from 'prop-types'

import API from '../../../../../api/api'
import ToggleField from '../../../../FormLegacy/FormFieldsV2/ToggleField'
import HourlyPricingSelector from '../components/HourlyPricingSelector'
import MultidayPricingSelector from '../components/MultidayPricingSelector'
import TextField from '../../../../FormLegacy/FormFieldsV2/TextField'
import SelectField from '../../../../FormLegacy/FormFieldsV2/SelectField'
import CustomQuestionsSelector from '../components/CustomQuestionsSelector'
import MediaUpload from '../../../../MediaUpload/MediaUpload'
import TextAreaField from '../../../../FormLegacy/FormFieldsV2/TextAreaField'
import EditorText from '../../../../EditorText/EditorText'
import {
  onChangeNumberField,
  getMarketplaceFormValues,
  makeValidationMarketplace,
  getPolicyTemplates,
  getAfterBookingTemplates,
  prepareMarketplacePayload,
  getAmenityMarketplaceCategories,
  AMENITY_OVERVIEW_FILE_LABEL,
  AMENITY_AGREEMENT_FILE_LABEL,
  ONE_HUNDRED_MB,
  MAX_FEE_NAME_LENGTH,
} from '../helpers'
import { useSnackbar } from '../../../../../helpers/hooks/useSnackbar'
import FormNavButtons from '../components/FormNavButtons'
import VideoPreviewModal from '../../../../VideoPreviewModal/VideoPreviewModal'

const MAX_AMENITY_NAME_LENGTH = 50

const AmenityMarketplace = ({ amenity }) => {
  const { t } = useTranslation(['reservations'])
  const { amenityId } = useParams()
  const { displaySnackbar } = useSnackbar()
  const policyTemplates = getPolicyTemplates(t)
  const afterBookingTemplates = getAfterBookingTemplates(t)
  const [amenityMarketplace, setAmenityMarketplace] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isVideoModalVisible, setIsVideoModalVisible] = useState(false)

  const handlePlayVideo = () => {
    setIsVideoModalVisible(true)
  }

  const handleCloseVideo = () => {
    setIsVideoModalVisible(false)
  }

  const fetchAmenityMarketplace = async () => {
    setIsLoading(true)
    try {
      const { data } = await API.getAmenityMarketplace(amenityId)
      if (typeof data === 'object' && Object.keys(data).length > 0) {
        setAmenityMarketplace(data)
      }
    } catch (error) {
      console.error('Error fetching amenity marketplace', error)
      displaySnackbar(t('reservations:amenityForms.marketplace.errorFetching'), true)
    } finally {
      setIsLoading(false)
    }
  }

  const validate = async (validateForm) => {
    const errors = await validateForm()
    if (Object.keys(errors).length > 0) {
      displaySnackbar(t('reservations:amenityForms.completeRequiredFields'), true)
      return false
    }
    return true
  }

  const handleSubmit = async (validateForm, values, { resetForm }) => {
    const isValid = await validate(validateForm)
    if (!isValid) return

    try {
      setIsSubmitting(true)

      const payload = prepareMarketplacePayload(values)
      await API.updateAmenityMarketplace(amenityId, payload)
      await fetchAmenityMarketplace()
      resetForm()
      displaySnackbar(t('reservations:amenityForms.marketplace.updatedSuccessfully'))
    } catch (error) {
      console.error('Error updating amenity marketplace', error)
      const message = error?.message || t('reservations:amenityForms.marketplace.errorUpdating')
      displaySnackbar(message, true)
    } finally {
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    fetchAmenityMarketplace()
  }, [])

  return (
    <>
      <Formik
        enableReinitialize={true}
        validateOnChange={false}
        initialValues={getMarketplaceFormValues(amenityMarketplace, amenity.multiDayBooking)}
        validationSchema={makeValidationMarketplace(t)}
      >
        {({ setFieldValue, handleChange, values, validateForm, errors, dirty, resetForm }) => {
          const handleOverviewUpdate = (file) => {
            if (file) {
              setFieldValue('overviewFile', file)
            }
          }

          const handleAgreementUpdate = (file) => {
            if (file) {
              setFieldValue('agreementFile', file)
            }
          }

          const handleApplyPolicyTemplate = (template) => {
            setFieldValue('cancellationPolicy', template.description)
            displaySnackbar(t('reservations:amenityForms.marketplace.templateApplied'))
          }

          const handleApplyAfterBookingTemplate = (template) => {
            setFieldValue('nextStepsAfterBooking', template.description)
            displaySnackbar(t('reservations:amenityForms.marketplace.templateApplied'))
          }

          return (
            <Form>
              <div className={`form-step ${isLoading ? 'is-loading' : ''}`}>
                <div className="form-step__content">
                  <div className="form-step__section">
                    <div className="form-step__row">
                      <div className="form-step__column">
                        <div className="form-step__label">
                          {t('reservations:amenityForms.marketplace.amenityName')}
                          <Info size={16} data-tip data-for="amenityNameTooltip" />
                          <ReactTooltip
                            className="tooltip"
                            multiline={true}
                            id="amenityNameTooltip"
                            place="top"
                            effect="solid"
                          >
                            {t('reservations:amenityForms.marketplace.amenityNameTooltip')}
                          </ReactTooltip>
                        </div>
                        <TextField
                          name="title"
                          value={values.title}
                          onChange={handleChange}
                          isError={!!errors.title}
                          maxLength={MAX_AMENITY_NAME_LENGTH}
                        />
                        <span className="input-desc-label">
                          {t('reservations:amenityForms.marketplace.maxInputLengthChars', {
                            count: MAX_AMENITY_NAME_LENGTH,
                          })}
                        </span>
                      </div>
                      <div className="form-step__column">
                        <div className="form-step__label">
                          {t('reservations:amenityForms.marketplace.maxStayLength')}{' '}
                          {values.multiDayBooking
                            ? t('reservations:amenityForms.marketplace.inDays')
                            : t('reservations:amenityForms.marketplace.inMinutes')}
                        </div>
                        <TextField
                          name="maxStayLength"
                          value={values.maxStayLength}
                          onChange={(e) => onChangeNumberField(e, handleChange)}
                          min={0}
                          onlyIntegers={true}
                          type="number"
                          isError={!!errors.maxStayLength}
                        />
                      </div>
                    </div>
                    <div className="form-step__row">
                      <SelectField
                        name="category"
                        label={t('reservations:amenityForms.marketplace.amenityCategory')}
                        value={values.category || ''}
                        options={getAmenityMarketplaceCategories(t)}
                        onChange={handleChange}
                        isError={!!errors.category}
                      />
                    </div>
                    <div className="form-step__row">
                      <div className="form-step__setting-box">
                        <div>
                          <div>
                            <div className="form-step__label">
                              {t('reservations:amenityForms.details.amenityOverview')}
                              <Info size={16} data-tip data-for="amenityOverview" />
                              <ReactTooltip
                                className="tooltip"
                                multiline={true}
                                id="amenityOverview"
                                place="top"
                                effect="solid"
                              >
                                {t('reservations:amenityForms.details.amenityOverviewTooltip')}
                              </ReactTooltip>
                            </div>
                          </div>
                          <div>
                            <div className="form-step__upload-container">
                              <MediaUpload
                                icon="arrowDownload"
                                mediaTypes={['pdf']}
                                onUpdate={handleOverviewUpdate}
                                onRemove={() => setFieldValue('overviewFile', null)}
                                persistedFileLabel={AMENITY_OVERVIEW_FILE_LABEL}
                                files={values.overviewFile ? [values.overviewFile] : []}
                                maxSize={ONE_HUNDRED_MB}
                                variant="lightgray"
                                buttonText={t(
                                  'reservations:singleReservationAmenityForm.uploadOverview',
                                )}
                              />
                              <span className="form-step__upload-desc">
                                {t('reservations:amenityForms.details.acceptedPdfFiles')}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="form-step__row">
                      <div className="form-step__setting-box">
                        <div>
                          <div>
                            <div className="form-step__label">
                              {t('reservations:amenityForms.details.amenityAgreement')}
                              <Info size={16} data-tip data-for="amenityAgreement" />
                              <ReactTooltip
                                className="tooltip"
                                multiline={true}
                                id="amenityAgreement"
                                place="top"
                                effect="solid"
                              >
                                {t('reservations:amenityForms.details.amenityAgreementTooltip')}
                              </ReactTooltip>
                            </div>
                            <button
                              type="button"
                              className="button button-blue"
                              onClick={handlePlayVideo}
                            >
                              <PlayCircle size={16} />
                              {t('reservations:amenityForms.details.watchTutorial')}
                            </button>
                          </div>
                          <div>
                            <div className="form-step__upload-container">
                              <MediaUpload
                                icon="arrowDownload"
                                mediaTypes={['pdf']}
                                onUpdate={handleAgreementUpdate}
                                onRemove={() => setFieldValue('agreementFile', null)}
                                persistedFileLabel={AMENITY_AGREEMENT_FILE_LABEL}
                                files={values.agreementFile ? [values.agreementFile] : []}
                                maxSize={ONE_HUNDRED_MB}
                                variant="lightgray"
                                buttonText={t(
                                  'reservations:singleReservationAmenityForm.uploadAgreement',
                                )}
                              />
                              <span className="form-step__upload-desc">
                                {t('reservations:amenityForms.details.acceptedPdfFiles')}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div>
                          <div>
                            <div className="form-step__label">
                              {t('reservations:amenityForms.details.mustSignBeforeBooking')}
                              <Info size={16} data-tip data-for="mustSignBeforeBooking" />
                              <ReactTooltip
                                className="tooltip"
                                multiline={true}
                                id="mustSignBeforeBooking"
                                place="top"
                                effect="solid"
                              >
                                {t(
                                  'reservations:amenityForms.details.mustSignBeforeBookingTooltip',
                                )}
                              </ReactTooltip>
                            </div>
                          </div>
                          <div>
                            <ToggleField
                              name="mustSignAgreement"
                              label={
                                values.mustSignAgreement
                                  ? t('reservations:amenityForms.yes')
                                  : t('reservations:amenityForms.no')
                              }
                              value={values.mustSignAgreement || false}
                              onChange={handleChange}
                              labelOnRight
                              disabled={!values.agreementFile}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="form-step__row">
                      <div className="form-step__column">
                        <TextAreaField
                          name="cancellationPolicy"
                          label={t(
                            'reservations:amenityForms.marketplace.cancellationRefundPolicy',
                          )}
                          onChange={handleChange}
                          value={values.cancellationPolicy || ''}
                          isError={!!errors.cancellationPolicy}
                        />
                        <span className="input-area-actions">
                          {t('reservations:amenityForms.marketplace.applyPolicyTemplates')}{' '}
                          {policyTemplates.map((template) => (
                            <span
                              key={template.name}
                              onClick={() => handleApplyPolicyTemplate(template)}
                            >
                              {template.name}
                            </span>
                          ))}
                        </span>
                      </div>
                    </div>
                    <div className="form-step__row">
                      <div className="form-step__column">
                        <div className="form-step__label">
                          {t(
                            'reservations:amenityForms.marketplace.amenityDescriptionForMarketplace',
                          )}{' '}
                          <span className="text-muted">
                            {t('reservations:amenityForms.marketplace.optional')}
                          </span>
                          <Info size={16} data-tip data-for="amenityDescriptionMarketplace" />
                          <ReactTooltip
                            className="tooltip"
                            multiline={true}
                            id="amenityDescriptionMarketplace"
                            place="top"
                            effect="solid"
                          >
                            {t('reservations:amenityForms.marketplace.amenityDescriptionTooltip')}
                          </ReactTooltip>
                        </div>
                        <EditorText
                          value={values.description || ''}
                          onChange={(content, _text) => setFieldValue('description', content)}
                          placeholder={t(
                            'reservations:amenityForms.marketplace.enterDescriptionPlaceholder',
                          )}
                        />
                      </div>
                    </div>
                    <div className="form-step__row">
                      <div className="form-step__column">
                        <div className="form-step__label">
                          {t('reservations:amenityForms.marketplace.nextStepsAfterBooking')}{' '}
                          <span className="text-muted">
                            {t('reservations:amenityForms.marketplace.optional')}
                          </span>
                        </div>
                        <EditorText
                          value={values.nextStepsAfterBooking || ''}
                          onChange={(content, _text) =>
                            setFieldValue('nextStepsAfterBooking', content)
                          }
                          placeholder={t(
                            'reservations:amenityForms.marketplace.provideInstructionsPlaceholder',
                          )}
                        />
                        <span className="input-area-actions input-area-actions--right">
                          {afterBookingTemplates.map((template) => (
                            <span
                              key={template.name}
                              onClick={() => handleApplyAfterBookingTemplate(template)}
                            >
                              {template.name}
                            </span>
                          ))}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="form-step__section">
                    <div className="form-step__row form-step__row--between">
                      <div>
                        <div className="form-step__label">
                          {t('reservations:amenityForms.marketplace.publishToMarketplace')}
                          <Info size={16} data-tip data-for="publishToMarketplaceTooltip" />
                          <ReactTooltip
                            className="tooltip"
                            multiline={true}
                            id="publishToMarketplaceTooltip"
                            place="top"
                            effect="solid"
                          >
                            {t('reservations:amenityForms.marketplace.publishToMarketplaceTooltip')}
                          </ReactTooltip>
                        </div>
                      </div>
                      <div>
                        <ToggleField
                          name="isActive"
                          label={
                            values.isActive
                              ? t('reservations:amenityForms.yes')
                              : t('reservations:amenityForms.no')
                          }
                          value={values.isActive}
                          onChange={handleChange}
                          labelOnRight
                        />
                      </div>
                    </div>

                    <div className="form-step__divider" />

                    <div className="form-step__row form-step__row--between">
                      <div>
                        <div className="form-step__label">
                          {t('reservations:amenityForms.marketplace.customFee')}
                          <Info size={16} data-tip data-for="customFeeMarketplaceTooltip" />
                          <ReactTooltip
                            className="tooltip"
                            multiline={true}
                            id="customFeeMarketplaceTooltip"
                            place="top"
                            effect="solid"
                          >
                            {t('reservations:amenityForms.marketplace.customFeeTooltip')}
                          </ReactTooltip>
                        </div>
                      </div>
                      <div>
                        <ToggleField
                          name="showCustomFee"
                          label={
                            values.showCustomFee
                              ? t('reservations:amenityForms.yes')
                              : t('reservations:amenityForms.no')
                          }
                          value={values.showCustomFee}
                          onChange={handleChange}
                          labelOnRight
                        />
                      </div>
                    </div>

                    {values.showCustomFee && (
                      <div className="form-step__row form-step__row--between">
                        <div className="form-step__column">
                          <TextField
                            label={t('reservations:amenityForms.pricing.feeNameRequired')}
                            name="customFeeName"
                            placeholder={t(
                              'reservations:amenityForms.marketplace.marketplaceFeeNamePlaceholder',
                            )}
                            value={values.customFeeName}
                            onChange={handleChange}
                            isError={errors.customFeeName}
                            maxLength={MAX_FEE_NAME_LENGTH}
                          />
                        </div>
                        <div className="form-step__column">
                          <TextField
                            label={t('reservations:amenityForms.pricing.feeAmountRequired')}
                            name="customFeeAmount"
                            placeholder={t(
                              'reservations:amenityForms.pricing.enterFeeAmountPlaceholder',
                            )}
                            value={values.customFeeAmount}
                            onChange={(e) => onChangeNumberField(e, handleChange)}
                            type="number"
                            min={0}
                            isError={errors.customFeeAmount}
                          />
                        </div>
                      </div>
                    )}

                    <div className="form-step__divider" />

                    {values.multiDayBooking && (
                      <>
                        <div className="form-step__row form-step__row--between">
                          <div>
                            <div className="form-step__label">
                              {t('reservations:amenityForms.marketplace.addCustomQuestions')}
                              <Info size={16} data-tip data-for="addCustomQuestionsTooltip" />
                              <ReactTooltip
                                className="tooltip"
                                multiline={true}
                                id="addCustomQuestionsTooltip"
                                place="top"
                                effect="solid"
                              >
                                {t(
                                  'reservations:amenityForms.marketplace.addCustomQuestionsTooltip',
                                )}
                              </ReactTooltip>
                            </div>
                          </div>
                          <div>
                            <ToggleField
                              name="showQuestions"
                              label={
                                values.showQuestions
                                  ? t('reservations:amenityForms.yes')
                                  : t('reservations:amenityForms.no')
                              }
                              value={values.showQuestions}
                              onChange={handleChange}
                              labelOnRight
                            />
                          </div>
                        </div>

                        {values.showQuestions && (
                          <CustomQuestionsSelector
                            values={values}
                            setFieldValue={setFieldValue}
                            errors={errors}
                          />
                        )}

                        <div className="form-step__divider" />
                      </>
                    )}

                    {/* TODO: Add custom messages */}
                    {/* <div className="form-step__row form-step__row--between">
                      <div>
                        <div className="form-step__label">
                          Add Custom Messages
                          <Info size={16} data-tip data-for="addCustomMessagesTooltip" />
                          <ReactTooltip
                            className="tooltip"
                            multiline={true}
                            id="addCustomMessagesTooltip"
                            place="top"
                            effect="solid"
                          >
                            Provide additional information or instructions that will be displayed to residents
                            during the booking process.
                          </ReactTooltip>
                        </div>
                      </div>
                      <div>
                        <ToggleField
                          name="addCustomMessages"
                          label={values.addCustomMessages ? 'Yes' : 'No'}
                          value={values.addCustomMessages}
                          onChange={handleChange}
                          labelOnRight
                        />
                      </div>
                    </div> */}

                    {/* <div className="form-step__divider" /> */}

                    <div className="form-step__row form-step__row--between">
                      <div>
                        <div className="form-step__label">
                          {t('reservations:amenityForms.marketplace.overrideAvailableDays')}
                          <Info size={16} data-tip data-for="overrideAvailableDaysTooltip" />
                          <ReactTooltip
                            className="tooltip"
                            multiline={true}
                            id="overrideAvailableDaysTooltip"
                            place="top"
                            effect="solid"
                          >
                            {t(
                              'reservations:amenityForms.marketplace.overrideAvailableDaysTooltip',
                            )}
                          </ReactTooltip>
                        </div>
                      </div>
                      <div>
                        <ToggleField
                          name="overrideAvailableDays"
                          label={
                            values.overrideAvailableDays
                              ? t('reservations:amenityForms.yes')
                              : t('reservations:amenityForms.no')
                          }
                          value={values.overrideAvailableDays}
                          onChange={handleChange}
                          labelOnRight
                        />
                      </div>
                    </div>

                    {values.overrideAvailableDays && (
                      <>
                        <div className="form-step__column">
                          {values.multiDayBooking ? (
                            <MultidayPricingSelector
                              values={values}
                              errors={errors}
                              handleChange={handleChange}
                              setFieldValue={setFieldValue}
                            />
                          ) : (
                            <HourlyPricingSelector
                              values={values}
                              errors={errors}
                              handleChange={handleChange}
                              setFieldValue={setFieldValue}
                            />
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <FormNavButtons
                onSubmit={() => handleSubmit(validateForm, values, { resetForm })}
                isSubmitting={isSubmitting}
                hasUnsavedChanges={dirty}
                isEdit
              />
            </Form>
          )
        }}
      </Formik>
      {isVideoModalVisible && (
        <VideoPreviewModal
          handleCloseVideo={handleCloseVideo}
          videoUrl="https://redvike-chefs.s3.us-east-1.amazonaws.com/amenityAgreement09102025.mp4"
        />
      )}
    </>
  )
}

AmenityMarketplace.propTypes = {
  amenity: PropTypes.object.isRequired,
}

export default AmenityMarketplace
